import { useMemo, useState } from 'react';
import {
  useAccountLedger,
  useChartOfAccountsList,
  useCreateChartOfAccount,
  useDeleteChartOfAccount,
  useImportRbmChart,
  useUpdateChartOfAccount,
  useIndustryTemplates,
  useApplyIndustryTemplate,
} from '../hooks/useChartOfAccounts';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import type { AccountType, ChartOfAccount, NormalBalance } from '../types';
import toast from 'react-hot-toast';

const ACCOUNT_TYPES: AccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];
const selectClass = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm';

const money = (value = 0, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);

export function ChartOfAccountsTab() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [active, setActive] = useState<'' | 'true' | 'false'>('');
  const [isCreating, setIsCreating] = useState(false);
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('generic');
  const [editingAccount, setEditingAccount] = useState<ChartOfAccount | null>(null);
  const [ledgerAccount, setLedgerAccount] = useState<ChartOfAccount | null>(null);
  const [ledgerDates, setLedgerDates] = useState({ startDate: '', endDate: '' });
  const { data: accounts = [], isLoading, error } = useChartOfAccountsList({ search, type, active });
  const { data: industryTemplates = [] } = useIndustryTemplates();
  const applyIndustry = useApplyIndustryTemplate();
  const ledger = useAccountLedger(ledgerAccount?._id || null, ledgerDates);
  const createAccount = useCreateChartOfAccount();
  const updateAccount = useUpdateChartOfAccount();
  const deleteAccount = useDeleteChartOfAccount();
  const importRbm = useImportRbmChart();

  const [formData, setFormData] = useState({
    code: '', name: '', type: 'asset' as AccountType, normalBalance: 'debit' as NormalBalance,
    description: '', openingBalance: '', parentId: '', currency: 'INR',
  });
  const [editFormData, setEditFormData] = useState({ name: '', description: '', isActive: true, parentId: '' });

  const rows = useMemo(() => {
    const byParent = new Map<string, ChartOfAccount[]>();
    accounts.forEach((account) => {
      const parent = account.parentId && accounts.some((item) => item._id === account.parentId) ? account.parentId : '';
      byParent.set(parent, [...(byParent.get(parent) || []), account]);
    });
    const result: Array<{ account: ChartOfAccount; depth: number }> = [];
    const visit = (parentId: string, depth: number, seen: Set<string>) => {
      (byParent.get(parentId) || []).sort((a, b) => a.code.localeCompare(b.code)).forEach((account) => {
        if (seen.has(account._id)) return;
        seen.add(account._id);
        result.push({ account, depth });
        visit(account._id, depth + 1, seen);
      });
    };
    visit('', 0, new Set());
    return result;
  }, [accounts]);

  if (isLoading) return <Skeleton className="h-[400px] w-full" />;
  if (error) return <div className="text-destructive">Failed to load Chart of Accounts.</div>;

  const resetCreate = () => setFormData({ code: '', name: '', type: 'asset', normalBalance: 'debit', description: '', openingBalance: '', parentId: '', currency: 'INR' });

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await createAccount.mutateAsync({ ...formData, parentId: formData.parentId || null, openingBalance: Number(formData.openingBalance) || 0 });
      setIsCreating(false);
      resetCreate();
      toast.success('Account created successfully.');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to create account.');
    }
  };

  const openEdit = (account: ChartOfAccount) => {
    setEditingAccount(account);
    setEditFormData({ name: account.name, description: account.description || '', isActive: account.isActive, parentId: account.parentId || '' });
  };

  const handleEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingAccount) return;
    try {
      await updateAccount.mutateAsync({ id: editingAccount._id, ...editFormData, parentId: editFormData.parentId || null });
      setEditingAccount(null);
      toast.success('Account updated successfully.');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update account.');
    }
  };

  const handleDelete = async (account: ChartOfAccount) => {
    if (!window.confirm(`Delete ${account.code} - ${account.name}?`)) return;
    try {
      await deleteAccount.mutateAsync(account._id);
      toast.success('Account deleted successfully.');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete account.');
    }
  };

  const handleImport = async () => {
    if (!window.confirm('Import the RBM Oil Corporation chart? Existing account codes will be skipped.')) return;
    try {
      const result = await importRbm.mutateAsync();
      toast.success(result.message);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to import chart.');
    }
  };

  const handleApplyIndustry = async () => {
    try {
      const result = await applyIndustry.mutateAsync(selectedIndustry);
      toast.success(result.message);
      setIsIndustryModalOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to apply industry template.');
    }
  };

  return (
    <>
      <Card className="flex h-full flex-col shadow-xs">
        <CardHeader className="flex-none border-b" title="Chart of Accounts" action={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 bg-blue-50/50 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 font-semibold" onClick={() => setIsIndustryModalOpen(true)}>
              ⚡ Setup Industry Chart
            </Button>
            <Button size="sm" variant="outline" onClick={handleImport} disabled={importRbm.isPending}>{importRbm.isPending ? 'Importing...' : 'Import RBM Chart'}</Button>
            <Button size="sm" onClick={() => setIsCreating(true)}>+ New Account</Button>
          </div>
        } />
        <CardBody className="flex-1 overflow-auto p-4">
          <div className="mb-4 grid gap-3 md:grid-cols-[1fr_180px_160px]">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or account name..." aria-label="Search accounts" />
            <select className={selectClass} value={type} onChange={(e) => setType(e.target.value)} aria-label="Account type">
              <option value="">All account types</option>
              {ACCOUNT_TYPES.map((item) => <option key={item} value={item}>{item[0].toUpperCase() + item.slice(1)}</option>)}
            </select>
            <select className={selectClass} value={active} onChange={(e) => setActive(e.target.value as typeof active)} aria-label="Account status">
              <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
            </select>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[850px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="px-4 py-2">Account</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Currency</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">Status</th><th className="px-4 py-2 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map(({ account, depth }) => (
                  <tr key={account._id} className={`border-t hover:bg-muted/30 ${!account.isActive ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-2" style={{ paddingLeft: `${16 + depth * 22}px` }}>
                      <span className="mr-3 font-mono text-muted-foreground">{account.code}</span>
                      <span className="font-medium">{account.name}</span>
                      {account.isSystem && <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase">System</span>}
                    </td>
                    <td className="px-3 py-2 capitalize">{account.type}</td>
                    <td className="px-3 py-2">{account.currency || 'INR'}</td>
                    <td className="px-3 py-2 text-right font-mono">{money(account.balance, account.currency)}</td>
                    <td className="px-3 py-2"><span className={account.isActive ? 'text-emerald-600' : 'text-muted-foreground'}>{account.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setLedgerAccount(account)}>Ledger</Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(account)}>Edit</Button>
                      {!account.isSystem && <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(account)} disabled={deleteAccount.isPending}>Delete</Button>}
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No accounts match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Modal open={isCreating} onClose={() => setIsCreating(false)} title="Create Ledger Account" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Account Code"><Input required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="e.g. 1010" /></Field>
            <Field label="Account Name"><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Cash" /></Field>
            <Field label="Account Type"><select className={selectClass} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value as AccountType, parentId: '' })}>{ACCOUNT_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
            <Field label="Normal Balance"><select className={selectClass} value={formData.normalBalance} onChange={(e) => setFormData({ ...formData, normalBalance: e.target.value as NormalBalance })}><option value="debit">Debit</option><option value="credit">Credit</option></select></Field>
            <Field label="Parent Account"><AccountOptions accounts={accounts} type={formData.type} value={formData.parentId} onChange={(parentId) => setFormData({ ...formData, parentId })} /></Field>
            <Field label="Currency"><Input maxLength={3} required value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })} /></Field>
            <Field label="Opening Balance"><Input type="number" min="0" step="0.01" value={formData.openingBalance} onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Description"><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button><Button type="submit" disabled={createAccount.isPending}>{createAccount.isPending ? 'Creating...' : 'Create Account'}</Button></div>
        </form>
      </Modal>

      <Modal open={!!editingAccount} onClose={() => setEditingAccount(null)} title="Edit Ledger Account">
        {editingAccount && <form onSubmit={handleEdit} className="space-y-4">
          <div className="rounded-md bg-muted/50 p-3"><span className="font-mono">{editingAccount.code}</span><span className="ml-3 capitalize text-muted-foreground">{editingAccount.type}</span>{editingAccount.isSystem && <span className="ml-2 text-xs">System account</span>}</div>
          <Field label="Account Name"><Input required value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} /></Field>
          <Field label="Parent Account"><AccountOptions accounts={accounts.filter((item) => item._id !== editingAccount._id)} type={editingAccount.type} value={editFormData.parentId} onChange={(parentId) => setEditFormData({ ...editFormData, parentId })} /></Field>
          <Field label="Description"><Input value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editFormData.isActive} disabled={editingAccount.isSystem} onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })} /> Account is active</label>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setEditingAccount(null)}>Cancel</Button><Button type="submit" disabled={updateAccount.isPending}>{updateAccount.isPending ? 'Saving...' : 'Save Changes'}</Button></div>
        </form>}
      </Modal>

      <Modal open={!!ledgerAccount} onClose={() => setLedgerAccount(null)} title={ledgerAccount ? `${ledgerAccount.code} - ${ledgerAccount.name}` : 'Account Ledger'} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3"><Field label="From"><Input type="date" value={ledgerDates.startDate} onChange={(e) => setLedgerDates({ ...ledgerDates, startDate: e.target.value })} /></Field><Field label="To"><Input type="date" value={ledgerDates.endDate} onChange={(e) => setLedgerDates({ ...ledgerDates, endDate: e.target.value })} /></Field></div>
          {ledger.isLoading ? <Skeleton className="h-40 w-full" /> : ledger.error ? <p className="text-destructive">Failed to load account ledger.</p> : <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[650px] text-sm"><thead className="bg-muted/50 text-left"><tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Voucher</th><th className="px-3 py-2">Narration</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th><th className="px-3 py-2 text-right">Balance</th></tr></thead>
              <tbody><tr className="border-t bg-muted/20"><td colSpan={5} className="px-3 py-2 font-medium">Opening balance</td><td className="px-3 py-2 text-right font-mono">{money(ledger.data?.openingBalance, ledgerAccount?.currency)}</td></tr>
                {ledger.data?.data.map((entry) => <tr key={entry._id} className="border-t"><td className="px-3 py-2">{new Date(entry.date).toLocaleDateString('en-IN')}</td><td className="px-3 py-2">{entry.voucherNumber || entry.voucherType}</td><td className="max-w-[220px] truncate px-3 py-2">{entry.narration || '-'}</td><td className="px-3 py-2 text-right font-mono">{entry.debit ? money(entry.debit, ledgerAccount?.currency) : '-'}</td><td className="px-3 py-2 text-right font-mono">{entry.credit ? money(entry.credit, ledgerAccount?.currency) : '-'}</td><td className="px-3 py-2 text-right font-mono">{money(entry.runningBalance, ledgerAccount?.currency)}</td></tr>)}
                {!ledger.data?.data.length && <tr className="border-t"><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No ledger activity in this period.</td></tr>}
              </tbody></table>
          </div>}
        </div>
      </Modal>

      {/* Industry Setup Modal */}
      <Modal open={isIndustryModalOpen} onClose={() => setIsIndustryModalOpen(false)} title="Setup Company Chart of Accounts by Industry" size="md">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select your industry template to seed a clean starting Chart of Accounts for this company. Existing accounts are preserved and duplicate control accounts are prevented.
          </p>
          <div className="space-y-2">
            {(industryTemplates || []).map((t) => (
              <label
                key={t.key}
                onClick={() => setSelectedIndustry(t.key)}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                  selectedIndustry === t.key
                    ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-950/40 ring-1 ring-blue-600'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-900 border-border'
                }`}
              >
                <input
                  type="radio"
                  name="industry"
                  value={t.key}
                  checked={selectedIndustry === t.key}
                  onChange={() => setSelectedIndustry(t.key)}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <span>{t.label}</span>
                    <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-muted-foreground px-1.5 py-0.5 rounded font-mono">
                      +{t.addsAccounts} accounts
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setIsIndustryModalOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary-dim text-white font-semibold"
              onClick={handleApplyIndustry}
              disabled={applyIndustry.isPending}
            >
              {applyIndustry.isPending ? 'Applying Chart...' : 'Apply Industry Chart'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block space-y-2"><span className="text-sm font-medium">{label}</span>{children}</label>;
}

function AccountOptions({ accounts, type, value, onChange }: { accounts: ChartOfAccount[]; type: AccountType; value: string; onChange: (value: string) => void }) {
  return <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}><option value="">No parent (top level)</option>{accounts.filter((item) => item.type === type).map((item) => <option key={item._id} value={item._id}>{item.code} - {item.name}</option>)}</select>;
}
